import type { CtState, GoalIntent, OnboardingSignal } from '@/lib/onboarding';

/****************
 * THE THREE WELCOME GUIDES. Mishka, 2026-10-21.
 *
 * Quick Learn EXPLAINS the platform; these DO the thing someone came for, one
 * step at a time: build a first strategy, find and copy a trader, connect
 * their own AI. Each goes as far as choosing where the orders go -- a key, or
 * paper trading, offered as equals -- and stops there. The guide never
 * presses Create Model, Start or the AI terms for anyone, and never promises
 * a gain.
 *
 * Deliberately NOT in GUIDES (orbGuides.ts): the Orb's model may launch any
 * guide named there, and these are started only by the welcome chooser.
 *
 * A step is shown on its route, around its targets (data-tour, the union of
 * the ones present). It advances when `done` turns true -- the page did the
 * thing -- or on Next. A step whose `done` is already true when it is reached
 * going forward is passed over: someone who already has a trader chosen is
 * not asked to choose one. `when` false removes the step for this run.
 * `sb` is the Strategy Builder step the fields live on (0 Template … 5 Review).
 * Texts are keys in learn.json (guide.*).
 ****************/

export interface GuideCtx {
  /** The latest value a page emitted, and the value when this step was reached. */
  now: <T = unknown>(s: OnboardingSignal) => T | undefined;
  entry: <T = unknown>(s: OnboardingSignal) => T | undefined;
  path: string;
}

export type GuideAction = 'next' | 'later' | 'done' | 'ai';

export interface GuideStep {
  /** Sent as onboarding_step_done's step. */
  id: string;
  /** Where it lives. null: anywhere (a card in the middle of the screen). */
  route: string | RegExp | null;
  /** Where "Take me there" goes when the person is elsewhere. */
  goTo?: string;
  /*
   * A trader's page has no one address. When no trader was opened in this
   * run, Take me there (and Next) open the first one of this list on `goTo`,
   * the way the list's own step does on Next.
   */
  pick?: string;
  targets?: string[];
  /** Pass over the step when none of its targets shows (an admin-only filter). */
  optional?: boolean;
  sb?: number;
  title: string;
  body: string;
  /** A sentence added when letting the AI decide is open on the account. */
  bodyAi?: string;
  done?: (c: GuideCtx) => boolean;
  when?: (c: GuideCtx) => boolean;
  /*
   * A control of the page that Next presses on this step, when the step's own
   * action only moves the screen (open a trader, Copy Trader). Never one that
   * commits anything. Its first button when the anchor is a list.
   */
  nextPress?: string;
  /** Only when this holds (the empty strategy picker's button, not the filled picker). */
  nextPressWhen?: (c: GuideCtx) => boolean;
  /** Buttons of a card in the middle; otherwise Back / Next. */
  actions?: GuideAction[];
}

/* ── What the conditions read ── */

const ct = (c: GuideCtx) => c.now<CtState>('ct_state');
const ctEntry = (c: GuideCtx) => c.entry<CtState>('ct_state');
const sbStep = (c: GuideCtx) => c.now<{ step: number }>('sb_step')?.step;
/** Something happened since this step was reached: the emitted value is a new one. */
const since = (s: OnboardingSignal) => (c: GuideCtx) => {
  const v = c.now(s);
  return v !== undefined && v !== c.entry(s);
};
const aiOpen = (c: GuideCtx) =>
  !!(c.now<{ available: boolean }>('ai_status')?.available || ct(c)?.aiAvailable);
const onTraderPage = /^\/traders\/[^/]+/;

/* ── Shared by the guides ── */

const destination = (id: string): GuideStep => ({
  // The destination and the Manage broker keys button under it (review #68, 10-25): the two ways, side by side.
  id, route: '/copy-trade', targets: ['ct-destination', 'ct-brokerkeys'],
  title: 'guide.dest.title', body: 'guide.dest.body',
  // A destination picked, or a key added while here.
  done: (c) => !!ct(c)?.destinationChosen
    || (ct(c)?.exchangesConnected ?? 0) > (ctEntry(c)?.exchangesConnected ?? Infinity),
});

const start = (id: string): GuideStep => ({
  id, route: '/copy-trade', targets: ['ct-start'],
  title: 'guide.start.title', body: 'guide.start.body',
  done: since('session_started'),
});

/** The builder, field by field. Shared by Strategy Builder (1-12) and the Copy Trading detour. */
const builderSteps = (p: string, when?: (c: GuideCtx) => boolean): GuideStep[] => [
  { id: `${p}template`, route: '/configure', sb: 0, targets: ['sb-scratch', 'sb-templates'],
    title: 'guide.sb.1.title', body: 'guide.sb.1.body', done: (c) => (sbStep(c) ?? 0) >= 1, when },
  { id: `${p}margin-cap`, route: '/configure', sb: 1, targets: ['sb-margin-cap'],
    title: 'guide.sb.2.title', body: 'guide.sb.2.body', when },
  { id: `${p}max-trades`, route: '/configure', sb: 1, targets: ['sb-max-trades'],
    title: 'guide.sb.3.title', body: 'guide.sb.3.body', when },
  { id: `${p}margin-type`, route: '/configure', sb: 1, targets: ['sb-margin-type'],
    title: 'guide.sb.4.title', body: 'guide.sb.4.body', when },
  { id: `${p}direction`, route: '/configure', sb: 1, targets: ['sb-direction'],
    title: 'guide.sb.5.title', body: 'guide.sb.5.body', when },
  { id: `${p}leverage`, route: '/configure', sb: 2, targets: ['sb-max-leverage'],
    title: 'guide.sb.6.title', body: 'guide.sb.6.body', when },
  { id: `${p}block-markets`, route: '/configure', sb: 2, targets: ['sb-block-markets'],
    title: 'guide.sb.7.title', body: 'guide.sb.7.body', when },
  { id: `${p}kill-switch`, route: '/configure', sb: 2, targets: ['sb-kill-switch'],
    title: 'guide.sb.8.title', body: 'guide.sb.8.body', when },
  { id: `${p}exec-strategy`, route: '/configure', sb: 3, targets: ['sb-exec-strategy'],
    title: 'guide.sb.9.title', body: 'guide.sb.9.body', when },
  { id: `${p}adds`, route: '/configure', sb: 3, targets: ['sb-increase', 'sb-decrease'],
    title: 'guide.sb.10.title', body: 'guide.sb.10.body', when },
  { id: `${p}exit`, route: '/configure', sb: 4, targets: ['sb-content'],
    title: 'guide.sb.11.title', body: 'guide.sb.11.body', when },
  { id: `${p}save`, route: '/configure', sb: 5, targets: ['sb-model-name', 'sb-create'],
    title: 'guide.sb.12.title', body: 'guide.sb.12.body', done: since('strategy_saved'), when },
];

/* ── 3.1 Strategy Builder: Build your first strategy ── */

const STRATEGY_BUILDER: GuideStep[] = [
  { id: 'intro', route: '/configure', title: 'guide.sb.0.title', body: 'guide.sb.0.body', actions: ['later', 'next'] },
  ...builderSteps(''),
  { id: 'trader', route: '/copy-trade', targets: ['ct-config'],
    title: 'guide.sb.13.title', body: 'guide.sb.13.body', done: (c) => !!ct(c)?.traderChosen },
  { id: 'apply', route: '/copy-trade', targets: ['ct-model'],
    title: 'guide.sb.14.title', body: 'guide.sb.14.body', done: (c) => !!ct(c)?.strategyChosen },
  destination('destination'),
  start('start'),
  { id: 'outro', route: null, title: 'guide.sb.17.title', body: 'guide.sb.17.body', actions: ['ai', 'done'] },
];

/* ── 3.2 Copy Trading: Find a trader worth copying ── */

/*
 * No strategy yet: the empty strategy picker leads to /configure?return=, and
 * the builder's steps run here, then back to Copy Trade with the new strategy
 * already applied (VirginDashboard's ?return= hands it over). Passed over when
 * the person picked a strategy, or the AI, without leaving the page.
 */
const detour = (c: GuideCtx) => {
  if (c.path === '/configure') return true;
  const s = ct(c);
  return !!s && !s.strategyChosen && !s.useAI && s.strategiesSaved === 0;
};

/*
 * The search starts on the Dashboard's Top Traders (Mishka, 2026-10-21, with
 * the remade video). Period, sort and sort direction are preferences kept on
 * the server (useSyncedPreference), so the guide never changes them: it waits
 * for the person to, and Next moves on without touching them.
 */
const COPY_TRADING: GuideStep[] = [
  { id: 'intro', route: '/', title: 'guide.ct.0.title', body: 'guide.ct.0.body', actions: ['later', 'next'] },
  { id: 'top', route: '/', goTo: '/', targets: ['dash-leaderboard'],
    title: 'guide.ct.top.title', body: 'guide.ct.top.body' },
  { id: 'period', route: '/', goTo: '/', targets: ['dash-period'],
    title: 'guide.ct.period.title', body: 'guide.ct.period.body', done: since('dash_period') },
  { id: 'sort', route: '/', goTo: '/', targets: ['dash-sort', 'dash-headers'],
    title: 'guide.ct.sort.title', body: 'guide.ct.sort.body', done: since('dash_sort') },
  { id: 'filters', route: '/', goTo: '/', targets: ['dash-filters', 'dash-filters-panel'],
    title: 'guide.ct.filters.title', body: 'guide.ct.filters.body', done: since('dash_filter') },
  { id: 'open', route: '/', goTo: '/', targets: ['dash-rows'],
    title: 'guide.ct.open.title', body: 'guide.ct.open.body', done: (c) => onTraderPage.test(c.path), nextPress: 'dash-rows' },
  { id: 'follow', route: onTraderPage, goTo: '/', pick: 'dash-rows', targets: ['tr-star', 'tr-vs'],
    title: 'guide.ct.5.title', body: 'guide.ct.5.body' },
  { id: 'numbers', route: onTraderPage, goTo: '/', pick: 'dash-rows', targets: ['tr-metrics'],
    title: 'guide.ct.6.title', body: 'guide.ct.6.body' },
  { id: 'how', route: onTraderPage, goTo: '/', pick: 'dash-rows', targets: ['tr-analysis'],
    title: 'guide.ct.7.title', body: 'guide.ct.7.body' },
  { id: 'consistency', route: onTraderPage, goTo: '/', pick: 'dash-rows', targets: ['tr-calendar'],
    title: 'guide.ct.8.title', body: 'guide.ct.8.body' },
  { id: 'copy', route: onTraderPage, goTo: '/', pick: 'dash-rows', targets: ['tr-copy-trader'],
    title: 'guide.ct.9.title', body: 'guide.ct.9.body', nextPress: 'tr-copy-trader',
    done: (c) => since('copy_trader')(c) || c.path === '/copy-trade' },
  { id: 'strategy', route: '/copy-trade', targets: ['ct-model', 'ct-ai-decide'],
    title: 'guide.ct.10.title', body: 'guide.ct.10.body', bodyAi: 'guide.ct.10.bodyAi',
    // No strategy yet: Next is "Create your first strategy", which comes back here with it applied.
    nextPress: 'ct-model', nextPressWhen: (c) => !!ct(c) && ct(c)!.strategiesSaved === 0 && !ct(c)!.useAI,
    done: (c) => !!ct(c)?.strategyChosen || !!ct(c)?.useAI || c.path === '/configure' },
  ...builderSteps('build-', detour),
  { id: 'apply', route: '/copy-trade', targets: ['ct-model'],
    title: 'guide.sb.14.title', body: 'guide.sb.14.body', done: (c) => !!ct(c)?.strategyChosen },
  destination('destination'),
  start('start'),
  { id: 'outro', route: null, title: 'guide.ct.13.title', body: 'guide.ct.13.body', actions: ['ai', 'done'] },
];

/* ── 3.3 AI Trading: Connect your AI ── */

const AI_TRADING: GuideStep[] = [
  { id: 'intro', route: '/ai/connect', title: 'guide.ai.0.title', body: 'guide.ai.0.body', actions: ['later', 'next'] },
  { id: 'two-things', route: '/ai/connect', goTo: '/ai/connect', targets: ['ai-cards'],
    title: 'guide.ai.1.title', body: 'guide.ai.1.body' },
  { id: 'name', route: '/ai/tensor', goTo: '/ai/tensor', targets: ['ai-token-name'],
    title: 'guide.ai.2.title', body: 'guide.ai.2.body' },
  { id: 'create', route: '/ai/tensor', goTo: '/ai/tensor', targets: ['ai-token-create'],
    title: 'guide.ai.3.title', body: 'guide.ai.3.body', done: since('token_created') },
  { id: 'copy', route: '/ai/tensor', goTo: '/ai/tensor', targets: ['ai-token-box'], optional: true,
    title: 'guide.ai.4.title', body: 'guide.ai.4.body', done: since('token_copied') },
  { id: 'desktop', route: '/ai/connect', goTo: '/ai/connect', targets: ['ai-desktop'],
    title: 'guide.ai.5.title', body: 'guide.ai.5.body' },
  { id: 'others', route: '/ai/connect', goTo: '/ai/connect', targets: ['ai-other'],
    title: 'guide.ai.6.title', body: 'guide.ai.6.body' },
  { id: 'ask', route: '/ai/tensor', goTo: '/ai/tensor', targets: ['ai-tokens'],
    title: 'guide.ai.7.title', body: 'guide.ai.7.body',
    done: (c) => (c.now<{ count: number }>('token_connected')?.count ?? 0) > (c.entry<{ count: number }>('token_connected')?.count ?? Infinity) },
  { id: 'catalogue', route: '/ai/tensor', goTo: '/ai/tensor', targets: ['ai-catalogue'],
    title: 'guide.ai.8.title', body: 'guide.ai.8.body' },
  // Letting the AI decide is on a list of accounts (AI_STRATEGY_USERS): say so, and end.
  { id: 'not-open', route: '/ai/connect', goTo: '/ai/connect', targets: ['ai-cards'],
    title: 'guide.ai.notOpen.title', body: 'guide.ai.notOpen.body', actions: ['done'],
    when: (c) => !aiOpen(c) },
  { id: 'ways', route: '/ai/connect', goTo: '/ai/connect', targets: ['ai-ways', 'ai-ways-list'],
    title: 'guide.ai.9.title', body: 'guide.ai.9.body', when: aiOpen },
  { id: 'trader', route: '/copy-trade', targets: ['ct-config'],
    title: 'guide.ai.10.title', body: 'guide.ai.10.body', when: aiOpen, done: (c) => !!ct(c)?.traderChosen },
  // useAI turns true only once THEY validated the terms (TradingNew): ticking alone is not enough.
  { id: 'decide', route: '/copy-trade', targets: ['ct-ai-decide'],
    title: 'guide.ai.11.title', body: 'guide.ai.11.body', when: aiOpen, done: (c) => !!ct(c)?.useAI },
  { id: 'which', route: '/copy-trade', targets: ['ct-which-ai'], optional: true,
    title: 'guide.ai.12.title', body: 'guide.ai.12.body', when: aiOpen },
  { id: 'paper', route: '/copy-trade', targets: ['ct-destination', 'ct-brokerkeys'],
    title: 'guide.ai.13.title', body: 'guide.ai.13.body', when: aiOpen, done: (c) => !!ct(c)?.destinationChosen },
  { id: 'outro', route: null, title: 'guide.ai.14.title', body: 'guide.ai.14.body', actions: ['done'], when: aiOpen },
];

/****************
 * GET STARTED, ON THE REAL LITE PAGES. Guillaume, 2026-10-31.
 *
 * "You didn't understand the speedrun... it must happen on the LITE UI." Get
 * Started was its own three screens; it is now this guide over the pages a
 * newcomer will use every day, in Lite: open a trader from Top Traders, Copy
 * Trader, a strategy (built in the Strategy Builder when there is none), the
 * Backtest tab and its Run, back to Go Live, paper trading, Start. The Orb's
 * card and spotlight (Quick Learn's look) walk it; the Orb on the screen keeps
 * its own nudges. Started by GetStartedGate for a new account, and by a speed
 * run, which is a new account seen by an admin.
 ****************/
const ctTab = (c: GuideCtx) => c.now<string>('ct_tab');
const FIRST_RUN: GuideStep[] = [
  { id: 'intro', route: null, title: 'guide.fr.0.title', body: 'guide.fr.0.body', actions: ['later', 'next'] },
  // The Dashboard as it is before anything runs: where the results will show.
  { id: 'dashboard', route: '/', goTo: '/', targets: ['dash-kpis'],
    title: 'guide.fr.dash.title', body: 'guide.fr.dash.body' },
  // Lite's own list (the Top Traders table is Pro's). Next opens its first trader, as 'open' does on the Dashboard.
  { id: 'featured', route: '/traders', goTo: '/traders', targets: ['traders-featured'], nextPress: 'traders-featured',
    title: 'guide.fr.featured.title', body: 'guide.fr.featured.body', done: (c) => onTraderPage.test(c.path) },
  // The trader's page, explained before copying (Guillaume, 10-31).
  { id: 'follow', route: onTraderPage, goTo: '/traders', pick: 'traders-featured', targets: ['tr-star', 'tr-vs'],
    title: 'guide.ct.5.title', body: 'guide.ct.5.body' },
  { id: 'numbers', route: onTraderPage, goTo: '/traders', pick: 'traders-featured', targets: ['tr-metrics'],
    title: 'guide.ct.6.title', body: 'guide.ct.6.body' },
  { id: 'how', route: onTraderPage, goTo: '/traders', pick: 'traders-featured', targets: ['tr-analysis'],
    title: 'guide.ct.7.title', body: 'guide.ct.7.body' },
  { id: 'consistency', route: onTraderPage, goTo: '/traders', pick: 'traders-featured', targets: ['tr-calendar'],
    title: 'guide.ct.8.title', body: 'guide.ct.8.body' },
  { id: 'copy', route: onTraderPage, goTo: '/traders', pick: 'traders-featured', targets: ['tr-copy-trader'],
    title: 'guide.ct.9.title', body: 'guide.ct.9.body', nextPress: 'tr-copy-trader',
    done: (c) => since('copy_trader')(c) || c.path === '/copy-trade' },
  { id: 'strategy', route: '/copy-trade', targets: ['ct-model', 'ct-ai-decide'],
    title: 'guide.ct.10.title', body: 'guide.ct.10.body', bodyAi: 'guide.ct.10.bodyAi',
    nextPress: 'ct-model', nextPressWhen: (c) => !!ct(c) && ct(c)!.strategiesSaved === 0 && !ct(c)!.useAI,
    done: (c) => !!ct(c)?.strategyChosen || !!ct(c)?.useAI || c.path === '/configure' },
  ...builderSteps('fr-build-', detour),
  { id: 'apply', route: '/copy-trade', targets: ['ct-model'],
    title: 'guide.sb.14.title', body: 'guide.sb.14.body', done: (c) => !!ct(c)?.strategyChosen || !!ct(c)?.useAI },
  // Test it first: the Backtest tab, the same trader and strategy, Run.
  { id: 'backtest-tab', route: '/copy-trade', targets: ['ct-tab-backtest'],
    title: 'guide.fr.bt.title', body: 'guide.fr.bt.body', nextPress: 'ct-tab-backtest',
    done: (c) => ctTab(c) === 'backtest' },
  // Optional: the backtester is admins' only (2026-10-15); everyone else sees Coming Soon, no form.
  { id: 'backtest-run', route: '/copy-trade', targets: ['bt-form', 'bt-run'], optional: true,
    title: 'guide.fr.run.title', body: 'guide.fr.run.body', done: since('backtest_started') },
  { id: 'live-tab', route: '/copy-trade', targets: ['ct-tab-live'],
    title: 'guide.fr.live.title', body: 'guide.fr.live.body', nextPress: 'ct-tab-live',
    done: (c) => ctTab(c) === 'live' },
  { id: 'paper', route: '/copy-trade', targets: ['ct-destination'],
    title: 'guide.fr.paper.title', body: 'guide.fr.paper.body', done: (c) => !!ct(c)?.destinationChosen },
  start('start'),
  { id: 'outro', route: null, title: 'guide.fr.end.title', body: 'guide.fr.end.body', actions: ['done'] },
];

export const ONBOARDING_GUIDES: Record<GoalIntent, GuideStep[]> = {
  strategy_builder: STRATEGY_BUILDER,
  copy_trading: COPY_TRADING,
  ai_trading: AI_TRADING,
  first_run: FIRST_RUN,
};

/** Where each guide opens: its welcome card is shown on the page it is about. */
export const GUIDE_HOME: Record<GoalIntent, string> = {
  strategy_builder: '/configure',
  copy_trading: '/',
  ai_trading: '/ai/connect',
  first_run: '/',
};

export const GUIDE_SECTION: Record<GoalIntent, string> = {
  strategy_builder: 'guide.sec.builder',
  copy_trading: 'guide.sec.copy',
  ai_trading: 'guide.sec.ai',
  first_run: 'guide.sec.firstRun',
};

/** Does the path belong to the step's route? */
export function onRoute(step: GuideStep, path: string): boolean {
  if (step.route === null) return true;
  return typeof step.route === 'string' ? path === step.route : step.route.test(path);
}
